import { useCallback, useState } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectActiveBattles } from "src/store/game"

import MiniMap from "src/components/MiniMap"
import { RadarIcon } from "src/components/icons"

import "./MapRadar.css"

/* The minimap, over the map instead of under the dock (owner's ask, 2026-09-04).

   It used to hold the bottom of the left dock, which cost the roster ~155px of permanent height —
   on a 1440x900 window that is two cities' worth of rows for a picture that answers "where am I"
   once a minute. So it moved onto the board it describes, in the corner furthest from the action
   bar, and the dock is the roster alone.

   The phone worked this out first and keeps its own version (`.phone-radar-chip` and the radar
   sheet in the Game view): the difference there is that the sheet *covers* the map.

   Open/closed and size are per-browser choices, remembered like the grid toggle — someone who
   plays with the radar shut should not have to shut it every round. */

/* A width in pixels, not a size name (owner's ask, 2026-09-04). Three named sizes needed three
   layouts, and the smallest of them stacked the controls under the map — which made the panel
   roughly square and stopped reading as a picture of a 2:1 world. One slider, one layout, and the
   world's ratio holds at every point on it because nothing but the width ever changes.

   The floor is what still fits a 34px column of controls beside a map worth looking at; the
   ceiling is a quarter of a 1440px map, past which the radar is competing with the board. */
const MIN_WIDTH = 168
const MAX_WIDTH = 420
const DEFAULT_WIDTH = 232
const OPEN_KEY = "hvz-radar-open"
const WIDTH_KEY = "hvz-radar-width"

const readStored = (key, fallback) => {
  try {
    return localStorage.getItem(key) ?? fallback
  } catch {
    // Storage unavailable (private window, blocked site data): this session's choice only.
    return fallback
  }
}

const store = (key, value) => {
  try {
    localStorage.setItem(key, value)
  } catch {
    // As above — the choice lives for this session.
  }
}

const MapRadar = ({ setClickOnMinimap, radarPosition, gridVisible, onToggleGrid }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(() => readStored(OPEN_KEY, "1") === "1")
  const [width, setWidth] = useState(() => {
    const stored = Number(readStored(WIDTH_KEY, ""))
    // A hand-edited or stale value must not be able to draw a radar nobody can see or dismiss.
    return Number.isFinite(stored) && stored > 0
      ? Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, stored))
      : DEFAULT_WIDTH
  })
  // The fires, so a shut radar still says the map has something on it worth opening for — the
  // same count the minimap draws as a badge when it is open.
  const battles = useSelector(selectActiveBattles)

  const toggle = useCallback(() => {
    setOpen((current) => {
      store(OPEN_KEY, current ? "0" : "1")
      return !current
    })
  }, [])

  const resize = useCallback((event) => {
    const next = Number(event.target.value)
    setWidth(next)
    store(WIDTH_KEY, String(next))
  }, [])

  if (!open) {
    return (
      <button type="button" className="map-radar-chip" onClick={toggle} aria-label={t("game.miniMap.alt")} title={t("game.miniMap.alt")}>
        <RadarIcon size={20} />
        {battles.length > 0 && <span className="map-radar-chip-fire">🔥</span>}
      </button>
    )
  }

  return (
    <div className="map-radar" style={{ "--radar-w": `${width}px` }}>
      <div className="map-radar-head">
        <input
          type="range"
          className="map-radar-slider"
          min={MIN_WIDTH}
          max={MAX_WIDTH}
          step={4}
          value={width}
          onChange={resize}
          aria-label={t("game.miniMap.resize")}
          title={t("game.miniMap.resize")}
        />
        <button
          type="button"
          className="map-radar-close"
          onClick={toggle}
          aria-label={t("game.miniMap.hide")}
          title={t("game.miniMap.hide")}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="m6 6 12 12M18 6 6 18" />
          </svg>
        </button>
      </div>
      <MiniMap
        setClickOnMinimap={setClickOnMinimap}
        radarPosition={radarPosition}
        gridVisible={gridVisible}
        onToggleGrid={onToggleGrid}
      />
    </div>
  )
}

export default MapRadar
